import React, { useState, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import { useSelector } from "react-redux";
import moment from "moment";
import useThemeColor from "../../../hooks/useThemeColor";
import MUITimePicker from "../MUITimePicker/MUITimePicker";
import FormInput from "../Input/formInput";
import "./scheduleStyle.css";
import "../MUITimePicker/MUITimePicker.css";

const CommonTimeSlotForAllWeek = ({ 
  setCommonTimeSlots, 
  commonTimeSlots, 
  isProfilePage = false,
  isStaffSchedule = false,
  commonBreak,
  setCommonBreak,
  preferTimePickerDropUp = false
}) => {
  // Get app settings and theme
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  const timeSlots = Array.isArray(commonTimeSlots)
    ? commonTimeSlots
    : [{ from: "", to: "", description: "" }];

  const handleCommonTimeSlotChange = (index, field, value) => {
    const formattedValue = moment.isMoment(value) ? value.format("HH:mm") : value;
    const newTimeSlots = timeSlots.map((slot, i) =>
      i === index ? { ...slot, [field]: formattedValue } : slot
    );
    setCommonTimeSlots(newTimeSlots);
  };

  const handleDescriptionChange = (index, value) => {
    const newTimeSlots = timeSlots.map((slot, i) =>
      i === index ? { ...slot, description: value } : slot
    );
    setCommonTimeSlots(newTimeSlots);
  };

  const handleAddTimeSlot = () => {
    if (isStaffSchedule) return; // No adding more slots for staff
    setCommonTimeSlots([...timeSlots, { from: "", to: "", description: "" }]);
  };

  const handleDeleteTimeSlot = (index) => {
    if (timeSlots.length > 1 || !isStaffSchedule) {
      setCommonTimeSlots(timeSlots.filter((_, i) => i !== index));
    }
  };

  const handleAddBreak = () => {
    setCommonBreak({ startBraekTime: "", breakMinutes: null, isBreakInclusive: false });
  };

  const handleRemoveBreak = () => {
    setCommonBreak(null);
  };

  const handleBreakStartChange = (value) => {
    const formattedValue = moment.isMoment(value) ? value.format("HH:mm") : (value || "");
    setCommonBreak(prev => ({ ...prev, startBraekTime: formattedValue }));
  };

  const handleBreakMinutesChange = (e) => {
    const value = e.target.value;
    const minutes = value === "" ? null : parseInt(value) || null;
    setCommonBreak(prev => ({ ...prev, breakMinutes: minutes }));
  };

  const handleBreakInclusiveChange = (checked) => {
    setCommonBreak(prev => ({ ...prev, isBreakInclusive: checked }));
  };

  const hasBreak = !!commonBreak;

  return (
    <div className="common-time-slots-container">
      {timeSlots.map((slot, index) => (
        <div
          className={`row align-items-center mb-2 ${isProfilePage ? "gx-3" : ""}`}
          key={index}
        >
          {isProfilePage && (
            <div className="col-3 d-flex align-items-center">
              <FormInput
                placeHolder="Services Name"
                type="text"
                value={slot.description || ""}
                onchange={(e) => handleDescriptionChange(index, e.target.value)}
                width="100%"
                className="form-control align-input"
              />
            </div>
          )}

          <div className={isProfilePage ? "col-3 d-flex align-items-center" : "col-3"}>
            <MUITimePicker
              label="Start Time"
              value={slot.from ? moment(slot.from, "HH:mm") : null}
              onChange={(time) => handleCommonTimeSlotChange(index, "from", time)}
              ampm={true}
              placeholder="Select start time"
              clearable={true}
              name={`start-time-${index}`}
              preferDropUp={preferTimePickerDropUp}
            />
          </div>

          <div
            className={isProfilePage ? "col-1 text-center d-flex align-items-center justify-content-center" : "col-2 text-center"}
          >
            <span 
              className={isProfilePage ? "fw-bold" : ""}
              style={{ color: isDarkMode ? '#ffffff' : '#000000' }}
            >
              to
            </span>
          </div>

          <div className={isProfilePage ? "col-3 d-flex align-items-center" : "col-5"}>
            <MUITimePicker
              label="End Time"
              value={slot.to ? moment(slot.to, "HH:mm") : null}
              onChange={(time) => handleCommonTimeSlotChange(index, "to", time)}
              ampm={true}
              placeholder="Select end time"
              clearable={true}
              name={`end-time-${index}`}
              preferDropUp={preferTimePickerDropUp}
            />
          </div>

          <div
            className={isProfilePage ? "col-1 d-flex align-items-center justify-content-center" : "col-1 d-flex align-items-center justify-content-center"}
          >
            {index !== 0 && (
              <button
                className="btn btn-sm rounded-pill px-3 py-2"
                onClick={() => handleDeleteTimeSlot(index)}
                type="button"
                style={{
                  backgroundColor: 'transparent',
                  color: '#dc3545',
                  border: '1px solid #dc3545',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.color = '#ffffff';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#dc3545';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <i className="fa fa-trash me-1"></i>
                Remove
              </button>
            )}
          </div>

          {/* Add/Remove Button Column */}
          <div
            className={isProfilePage ? "col-1 d-flex align-items-center justify-content-center" : "col-1 d-flex align-items-center justify-content-center"}
          >
            {index === timeSlots.length - 1 && (
              <>
                {!isStaffSchedule && (
                  <button
                    className="btn btn-sm rounded-pill px-3 py-2"
                    onClick={handleAddTimeSlot}
                    type="button"
                    style={{
                      backgroundColor: 'transparent',
                      color: themeColor,
                      border: `1px solid ${themeColor}`,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = themeColor;
                      e.target.style.color = '#ffffff';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = themeColor;
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fa fa-plus me-1"></i>
                    Add Slot
                  </button>
                )}
                {isStaffSchedule && !hasBreak && (
                  <button
                    className="btn btn-sm rounded-pill px-3 py-2"
                    onClick={handleAddBreak}
                    type="button"
                    style={{
                      backgroundColor: 'transparent',
                      color: themeColor,
                      border: `1px solid ${themeColor}`,
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = themeColor;
                      e.target.style.color = '#ffffff';
                      e.target.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = themeColor;
                      e.target.style.transform = 'translateY(0)';
                    }}
                  >
                    <i className="fa fa-plus me-1"></i>
                    Add Break
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      ))}

      {/* Break Fields Row - Only show when hasBreak for staff */}
      {isStaffSchedule && hasBreak && (
        <>
          <div className="row align-items-center mb-2 gx-3">
            <div className="col-3 d-flex align-items-center">
              <MUITimePicker
                label="Start Break Time"
                value={commonBreak.startBraekTime ? moment(commonBreak.startBraekTime, "HH:mm") : null}
                onChange={handleBreakStartChange}
                ampm={true}
                placeholder="Select start break time"
                clearable={true}
                name="start-break-time"
              />
            </div>

            <div className="col-2 text-center d-flex align-items-center justify-content-center">
              {/* Empty spacer */}
            </div>

            <div className="col-5 d-flex flex-column align-items-start">
              <label className="fw-bold mb-1" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                Break Minutes
              </label>
              <FormInput
                placeHolder="Break Minutes"
                type="number"
                min="0"
                value={commonBreak.breakMinutes || ""}
                onchange={handleBreakMinutesChange}
                width="100%"
                className="form-control align-input"
              />
            </div>

            <div className="col-1 d-flex align-items-center justify-content-center mt-4">
              <button
                className="btn btn-sm rounded-pill px-3 py-2"
                onClick={handleRemoveBreak}
                type="button"
                style={{
                  backgroundColor: 'transparent',
                  color: '#dc3545',
                  border: '1px solid #dc3545',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#dc3545';
                  e.target.style.color = '#ffffff';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#dc3545';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <i className="fa fa-trash me-1"></i>
                Remove
              </button>
            </div>

            <div className="col-1"></div> 
          </div>
          
          {/* Break Inclusive Toggle Row */}
          <div className="row align-items-center mb-3 gx-3">
            <div className="col-3"></div>
            <div className="col-2"></div>
            <div className="col-5">
              <div className="d-flex align-items-center">
                <input
                  type="checkbox"
                  id="break-inclusive-common"
                  checked={commonBreak.isBreakInclusive || false}
                  onChange={(e) => handleBreakInclusiveChange(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    marginRight: '10px',
                    cursor: 'pointer',
                    accentColor: themeColor
                  }}
                />
                <label 
                  htmlFor="break-inclusive-common" 
                  style={{ 
                    color: isDarkMode ? '#ffffff' : '#000000',
                    marginBottom: 0,
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  Break time is included in shift hours
                </label>
              </div>
            </div>
            <div className="col-2"></div>
          </div>
        </>
      )}
    </div>
  );
};

export default CommonTimeSlotForAllWeek;